import { useContext } from "react";
import { ValidationError, validationFailed, ValidationResult } from "sprint-common-web";

import { CreateEntityResponse } from "../../../common/api/common/CreateEntityResponse";
import { AppConfigContext } from "../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../core/data-access/useAuthorizedFetch";
import { CreateEpisodeRequest } from "./CreateEpisodeRequest";
import { CreateEpisodeVersionRequest } from "./CreateEpisodeVersionRequest";
import { CreateSeasonRequest } from "./CreateSeasonRequest";
import { CreateTvSeriesRequest } from "./CreateTvSeriesRequest";
import { GetTvSeriesByIdResponse } from "./GetTvSeriesByIdResponse";
import { GetTvSeriesBySimilarTitleResponse } from "./GetTvSeriesBySimilarTitleResponse";
import { GetTvSeriesListRequest } from "./GetTvSeriesListRequest";
import { GetTvSeriesListResponse } from "./GetTvSeriesListResponse";
import { MergeEpisodesRequest } from "./MergeEpisodesRequest";
import { MergeEpisodesResponse } from "./MergeEpisodesResponse";
import { MergeEpisodeVersionsRequest } from "./MergeEpisodeVersionsRequest";
import { MergeEpisodeVersionsResponse } from "./MergeEpisodeVersionsResponse";
import { MergeSeasonsRequest } from "./MergeSeasonsRequest";
import { MergeSeasonsResponse } from "./MergeSeasonsResponse";
import { MergeTvSeriesRequest } from "./MergeTvSeriesRequest";
import { MergeTvSeriesResponse } from "./MergeTvSeriesResponse";
import { UpdateEpisodeRequest } from "./UpdateEpisodeRequest";
import { UpdateEpisodeVersionRequest } from "./UpdateEpisodeVersionRequest";
import { UpdateSeasonRequest } from "./UpdateSeasonRequest";
import { UpdateTvSeriesDetailsRequest } from "./UpdateTvSeriesDetailsRequest";
import { ValidateCreateEpisodeVersionRequest } from "./ValidateCreateEpisodeVersionRequest";
import { ValidateMergeEpisodesRequest } from "./ValidateMergeEpisodesRequest";
import { ValidateMergeEpisodeVersionsRequest } from "./ValidateMergeEpisodeVersionsRequest";
import { ValidateMergeEpisodeVersionsResponse } from "./ValidateMergeEpisodeVersionsResponse";
import { ValidateMergeSeasonsRequest } from "./ValidateMergeSeasonsRequest";
import { ValidateMergeTvSeriesRequest } from "./ValidateMergeTvSeriesRequest";
import { ValidateTvSeriesTitleRequest } from "./ValidateTvSeriesTitleRequest";
import { ValidateUpdateEpisodeVersionRequest } from "./ValidateUpdateEpisodeVersionRequest";

export const useAssetsManageTvSeriesApi = () => {
    const appConfigContext = useContext(AppConfigContext);
    const { httpGet, httpPost, httpPut } = useAuthorizedFetch();
    const baseUrl = `${appConfigContext.catalogueConfig.apiUrl}/tv-series/manage`;

    const getTvSeriesList = async (query: GetTvSeriesListRequest): Promise<GetTvSeriesListResponse> => {
        return httpGet<GetTvSeriesListResponse>(`${baseUrl}/search`, query);
    };

    const createTvSeries = async (query: CreateTvSeriesRequest): Promise<CreateEntityResponse> => {
        return httpPost<CreateEntityResponse>(`${baseUrl}`, query);
    };

    const getTvSeriesById = async (tvSeriesId: string): Promise<GetTvSeriesByIdResponse> => {
        return httpGet<GetTvSeriesByIdResponse>(`${baseUrl}/${tvSeriesId}`);
    };

    const updateTvSeriesDetails = async (
        tvSeriesId: string,
        updateTvSeriesDetailsRequest: UpdateTvSeriesDetailsRequest
    ): Promise<void> => {
        return httpPut(`${baseUrl}/${tvSeriesId}`, updateTvSeriesDetailsRequest);
    };

    const createSeason = async (
        tvSeriesId: string,
        createSeasonRequest: CreateSeasonRequest
    ): Promise<CreateEntityResponse> => {
        return httpPut<CreateEntityResponse>(`${baseUrl}/${tvSeriesId}/seasons`, createSeasonRequest);
    };

    const updateSeason = async (
        tvSeriesId: string,
        seasonId: string,
        updateSeasonRequest: UpdateSeasonRequest
    ): Promise<CreateEntityResponse> => {
        return httpPut<CreateEntityResponse>(`${baseUrl}/${tvSeriesId}/seasons/${seasonId}`, updateSeasonRequest);
    };

    const createEpisode = async (createEpisodeRequest: CreateEpisodeRequest): Promise<CreateEntityResponse> => {
        return httpPost<CreateEntityResponse>(`${baseUrl}/episodes`, createEpisodeRequest);
    };

    const updateEpisode = async (
        episodeId: string,
        updateEpisodeRequest: UpdateEpisodeRequest
    ): Promise<CreateEntityResponse> => {
        return httpPut<CreateEntityResponse>(`${baseUrl}/episodes/${episodeId}`, updateEpisodeRequest);
    };

    const validateCreateEpisodeVersion = async (
        validateCreateEpisodeVersionRequest: ValidateCreateEpisodeVersionRequest
    ): Promise<ValidationResult> => {
        return httpGet(
            `${baseUrl}/${validateCreateEpisodeVersionRequest.tvSeriesId}/versions/validate-create`,
            validateCreateEpisodeVersionRequest
        );
    };

    const createEpisodeVersion = async (
        createEpisodeVersionRequest: CreateEpisodeVersionRequest,
        episodeId: string
    ): Promise<CreateEntityResponse> => {
        return httpPut<CreateEntityResponse>(`${baseUrl}/${episodeId}/versions`, createEpisodeVersionRequest);
    };

    const validateUpdateEpisodeVersion = async (
        episodeId: string,
        versionId: string,
        details: ValidateUpdateEpisodeVersionRequest
    ): Promise<ValidationResult> => {
        try {
            return await httpGet(`${baseUrl}/episodes/${episodeId}/versions/${versionId}/validate-update`, details);
        } catch (error) {
            if (error instanceof ValidationError) return validationFailed(error.failures);

            throw error;
        }
    };

    const updateEpisodeVersion = async (
        episodeId: string,
        versionId: string,
        details: UpdateEpisodeVersionRequest
    ): Promise<void> => {
        return httpPut(`${baseUrl}/episodes/${episodeId}/versions/${versionId}`, details);
    };

    const getTvSeriesBySimilarTitle = async (
        request: ValidateTvSeriesTitleRequest,
        signal: AbortSignal
    ): Promise<GetTvSeriesBySimilarTitleResponse> => {
        return httpGet<GetTvSeriesBySimilarTitleResponse>(`${baseUrl}/similar`, request, signal);
    };

    const validateMergeEpisodeVersions = async (
        tvSeriesId: string,
        request: ValidateMergeEpisodeVersionsRequest
    ): Promise<ValidateMergeEpisodeVersionsResponse> => {
        return httpGet<ValidateMergeEpisodeVersionsResponse>(
            `${baseUrl}/${tvSeriesId}/versions/validate-merge`,
            request
        );
    };

    const mergeEpisodeVersions = async (
        tvSeriesId: string,
        request: MergeEpisodeVersionsRequest
    ): Promise<MergeEpisodeVersionsResponse> => {
        return httpPut<MergeEpisodeVersionsResponse>(`${baseUrl}/${tvSeriesId}/versions/merge`, request);
    };

    const validateMergeEpisodes = async (
        tvSeriesId: string,
        request: ValidateMergeEpisodesRequest
    ): Promise<ValidationResult> => {
        return httpGet<ValidationResult>(`${baseUrl}/${tvSeriesId}/episodes/validate-merge`, request);
    };

    const mergeEpisodes = async (tvSeriesId: string, request: MergeEpisodesRequest): Promise<MergeEpisodesResponse> => {
        return httpPut<MergeEpisodesResponse>(`${baseUrl}/${tvSeriesId}/episodes/merge`, request);
    };

    const validateMergeSeasons = async (
        tvSeriesId: string,
        request: ValidateMergeSeasonsRequest
    ): Promise<ValidationResult> => {
        return httpGet<ValidationResult>(`${baseUrl}/${tvSeriesId}/seasons/validate-merge`, request);
    };

    const mergeSeasons = async (tvSeriesId: string, request: MergeSeasonsRequest): Promise<MergeSeasonsResponse> => {
        return httpPut<MergeSeasonsResponse>(`${baseUrl}/${tvSeriesId}/seasons/merge`, request);
    };

    const validateMergeTvSeries = async (request: ValidateMergeTvSeriesRequest): Promise<ValidationResult> => {
        return httpGet<ValidationResult>(`${baseUrl}/validate-merge`, request);
    };

    const mergeTvSeries = async (request: MergeTvSeriesRequest): Promise<MergeTvSeriesResponse> => {
        return httpPut<MergeTvSeriesResponse>(`${baseUrl}/merge`, request);
    };

    return {
        getTvSeriesList,
        createTvSeries,
        getTvSeriesById,
        updateTvSeriesDetails,
        createSeason,
        updateSeason,
        createEpisode,
        updateEpisode,
        validateCreateEpisodeVersion,
        createEpisodeVersion,
        validateUpdateEpisodeVersion,
        updateEpisodeVersion,
        getTvSeriesBySimilarTitle,
        validateMergeEpisodeVersions,
        mergeEpisodeVersions,
        validateMergeEpisodes,
        mergeEpisodes,
        validateMergeSeasons,
        mergeSeasons,
        validateMergeTvSeries,
        mergeTvSeries
    };
};
